<template>
    <back-button :route="{ name: 'finnancial.cashout-control' }" />
    <div class="my-3 flex flex-col">
        <user-heading
            v-if="storeCashout"
            :user="storeCashout.user"
        ></user-heading>
        <div class="flex items-center gap-3">
            <h2 class="tex text-lg font-bold capitalize dark:text-white">
                Saque #{{ storeCashout.id }}

                <cashout-tags :cashout="storeCashout" />
            </h2>
            <submit-button
                v-if="storeCashout"
                size="sm"
                :loading="loadingExport"
                @click="exportReconciliation"
            >
                <ArrowDownTrayIcon class="mr-1.5 h-4 w-4" />
                Exportar Conciliacao
            </submit-button>
        </div>
    </div>
    <div class="grid grid-cols-3 gap-4">
        <div class="col-span-2">
            <commission-cards :user="storeCashout.user"></commission-cards>

            <chargeback-risk-badge
                v-if="storeCashout && storeCashout.id"
                class="mb-4"
                :cashout-id="Number(storeCashout.id)"
            />
            <h3
                class="align-center flex text-lg font-bold text-gray-400"
            >
                Saldo
                <small
                    class="ml-auto text-indigo-500 hover:cursor-pointer hover:underline"
                    @click="balanceChart = !balanceChart"
                >
                    Grafico
                </small>
            </h3>
            <balance-schedule
                class="my-3"
                v-if="storeCashout && balanceChart"
                :user="storeCashout.user"
            ></balance-schedule>
            <balance-cards
                v-if="storeCashout && walletFromCashout"
                :user="storeCashout.user"
                :wallet="walletFromCashout"
            ></balance-cards>

            <h3
                class="align-center mt-0 flex text-lg font-bold text-gray-400"
            >
                Conta

                <update-bank-account
                    v-if="storeCashout && hasPermission('update.bankAccounts')"
                    :bank-account="storeCashout.bank_account"
                    @updated="
                        fetchCashout();
                        fetchStatus();
                    "
                />
            </h3>
            <div
                class="mb-4 w-full min-w-0 overflow-hidden rounded-lg bg-white drop-shadow-sm dark:bg-gray-800"
            >
                <div
                    class="w-full min-w-0 overflow-hidden rounded-lg bg-white p-5 drop-shadow-sm dark:bg-gray-800"
                >
                    <col-skeleton
                        v-if="loading"
                        :cols="3"
                        class="mb-4"
                    ></col-skeleton>
                    <div
                        class="mb-4 grid grid-cols-4 gap-3"
                        v-if="storeCashout"
                    >
                        <info-col title="CADASTRADO EM" :loading="loading">
                            {{ storeCashout.user.created_at }}
                        </info-col>
                        <info-col title="ÚLTIMO SAQUE" :loading="loading">
                            {{ storeCashout.last_cashout }}
                        </info-col>
                        <info-col
                            title="SAQUES REALIZADOS"
                            :loading="loading"
                            class="w-full"
                        >
                            <div class="flex flex-row">
                                <div
                                    v-for="cashoutCount in storeCashout.cashout_count"
                                    v-tooltip="
                                        getCashoutStatusName(
                                            cashoutCount.status,
                                        )
                                    "
                                    class="mr-1 flex items-center"
                                >
                                    <component
                                        :is="countIcon(cashoutCount.status)"
                                        class="mr-1 h-5 w-5 shrink-0 self-center"
                                    />
                                    <span>{{ cashoutCount.count }}</span>
                                </div>
                            </div>
                        </info-col>
                    </div>
                    <col-skeleton
                        v-if="loading"
                        :cols="4"
                        class="mt-5"
                    ></col-skeleton>
                    <div class="grid grid-cols-4 gap-3" v-if="storeCashout">
                        <info-col title="NOME FAVORECIDO">
                            {{ storeCashout.bank_account.holder_name }}
                        </info-col>
                        <info-col title="NATUREZA">
                            {{
                                storeCashout.user.type === "company"
                                    ? "JURÍDICA"
                                    : "FÍSICA"
                            }}
                        </info-col>
                        <info-col title="CPF/CNPJ">
                            {{
                                storeCashout.user.type === "company"
                                    ? formatCnpj(storeCashout.user.cnpj)
                                    : formatCpf(storeCashout.user.cpf)
                            }}
                        </info-col>
                        <info-col title="BANCO" v-if="storeCashout.bank_account.receiver_type === 'bank'">
                            {{
                                formatBankCode(
                                    storeCashout.bank_account?.bank?.code,
                                )
                            }}
                            -
                            {{ storeCashout.bank_account?.bank?.name }}
                        </info-col>
                        <info-col title="Chave PIX" v-if="storeCashout.bank_account.receiver_type === 'pix'">
                            <span
                                v-if="feature_flag_is_active && pixValidationTooltip"
                                v-tooltip="pixValidationTooltip"
                                class="inline-flex items-center gap-1.5"
                            >
                                {{ storeCashout.bank_account?.pix_key }}
                                <CheckCircleIcon
                                    v-if="pixValidationCompleted"
                                    class="h-4 w-4 shrink-0 text-green-500"
                                    aria-hidden="true"
                                />
                            </span>
                            <template v-else>
                                {{ storeCashout.bank_account?.pix_key }}
                            </template>
                        </info-col>

                        <info-col title="TIPO DE CONTA" v-if="storeCashout.bank_account.receiver_type === 'bank'">
                            {{
                                storeCashout.bank_account.type === "checking"
                                    ? "Corrente"
                                    : "Poupança"
                            }}
                        </info-col>
                        <info-col title="AGENCIA" v-if="storeCashout.bank_account.receiver_type === 'bank'">
                            {{ storeCashout.bank_account.branch_number }}
                            <span
                                v-if="
                                    storeCashout.bank_account.branch_check_digit
                                "
                            >
                                -
                                {{
                                    storeCashout.bank_account.branch_check_digit
                                }}
                            </span>
                        </info-col>
                        <info-col title="CONTA" v-if="storeCashout.bank_account.receiver_type === 'bank'">
                            {{ storeCashout.bank_account.account_number }} -
                            {{ storeCashout.bank_account.account_check_digit }}
                        </info-col>
                        <info-col title="CADASTRADA EM">
                            {{ storeCashout.bank_account.created_at }}
                        </info-col>
                    </div>
                </div>
            </div>

            <div
                class="mb-4 w-full min-w-0 overflow-hidden rounded-lg bg-white p-5 drop-shadow-sm dark:bg-gray-800"
            >
                <col-skeleton v-if="loading" :cols="3"></col-skeleton>
                <div class="grid grid-cols-4 gap-3" v-if="storeCashout">
                    <info-col title="VALOR SOLICITADO">
                        {{ $root.formatCurrency(storeCashout.amount) }}
                    </info-col>
                    <info-col title="TAXA APLICADA">
                        {{ $root.formatCurrency(storeCashout.fee) }}
                    </info-col>
                    <info-col title="VALOR A RECEBER">
                        {{ $root.formatCurrency(storeCashout.net_value) }}
                    </info-col>
                    <info-col title="TOTAL SACADO">
                        {{ $root.formatCurrency(totalCashoutAmount) }}
                    </info-col>
                </div>
            </div>

            <h3
                v-if="storeCashout && storeCashout.withdrawal_policy"
                class="align-center mt-0 flex text-lg font-bold text-gray-400"
            >
                Política de Saque
            </h3>
            <div
                v-if="storeCashout && storeCashout.withdrawal_policy"
                class="mb-4 w-full min-w-0 overflow-hidden rounded-lg bg-white drop-shadow-sm dark:bg-gray-800"
            >
                <div class="w-full min-w-0 overflow-hidden rounded-lg bg-white p-5 drop-shadow-sm dark:bg-gray-800">
                    <!-- Estado e Limites -->
                    <div class="mb-5 grid grid-cols-4 gap-3">
                        <info-col title="ESTADO DA CONTA">
                            <span
                                class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
                                :class="{
                                    'bg-green-500': storeCashout.withdrawal_policy.account_state === 'ACTIVE',
                                    'bg-red-500': storeCashout.withdrawal_policy.account_state === 'BLOCKED',
                                    'bg-gray-500': storeCashout.withdrawal_policy.account_state === 'DORMANT',
                                    'bg-yellow-500': storeCashout.withdrawal_policy.account_state === 'REACTIVATED',
                                }"
                            >
                                {{ storeCashout.withdrawal_policy.account_state }}
                            </span>
                        </info-col>
                        <info-col title="LIMITE DE SAQUE">
                            {{ storeCashout.withdrawal_policy.cashout_limit_cents != null ? $root.formatCurrency(storeCashout.withdrawal_policy.cashout_limit_cents) : '-' }}
                        </info-col>
                        <info-col title="DIAS DESDE ÚLTIMO SAQUE">
                            {{ storeCashout.withdrawal_policy.days_since_last_cashout ?? '-' }}
                        </info-col>
                        <info-col title="MÉTODO CÁLCULO LIMITE">
                            <span class="capitalize">{{ storeCashout.withdrawal_policy.limit_calculation_method ?? '-' }}</span>
                        </info-col>
                    </div>

                    <!-- Flags de Controle -->
                    <div class="mb-5 grid grid-cols-4 gap-3">
                        <info-col title="ANTECIPAÇÃO PERMITIDA">
                            <span class="inline-flex items-center gap-1" :class="storeCashout.withdrawal_policy.anticipation_allowed ? 'text-green-500' : 'text-red-500'">
                                <component :is="storeCashout.withdrawal_policy.anticipation_allowed ? 'CheckCircleIcon' : 'NoSymbolIcon'" class="h-4 w-4" />
                                {{ storeCashout.withdrawal_policy.anticipation_allowed ? 'Sim' : 'Não' }}
                            </span>
                        </info-col>
                        <info-col title="CASHOUT BLOQUEADO">
                            <span class="inline-flex items-center gap-1" :class="storeCashout.withdrawal_policy.block_cashout ? 'text-red-500' : 'text-green-500'">
                                <component :is="storeCashout.withdrawal_policy.block_cashout ? 'NoSymbolIcon' : 'CheckCircleIcon'" class="h-4 w-4" />
                                {{ storeCashout.withdrawal_policy.block_cashout ? 'Sim' : 'Não' }}
                            </span>
                        </info-col>
                        <info-col title="CASHOUT TRAVADO">
                            <span class="inline-flex items-center gap-1" :class="storeCashout.withdrawal_policy.settings_is_cashout_locked ? 'text-red-500' : 'text-green-500'">
                                <component :is="storeCashout.withdrawal_policy.settings_is_cashout_locked ? 'NoSymbolIcon' : 'CheckCircleIcon'" class="h-4 w-4" />
                                {{ storeCashout.withdrawal_policy.settings_is_cashout_locked ? 'Sim' : 'Não' }}
                            </span>
                        </info-col>
                        <info-col title="PODE ANTECIPAR (SETTINGS)">
                            <span class="inline-flex items-center gap-1" :class="storeCashout.withdrawal_policy.settings_can_anticipate ? 'text-green-500' : 'text-red-500'">
                                <component :is="storeCashout.withdrawal_policy.settings_can_anticipate ? 'CheckCircleIcon' : 'NoSymbolIcon'" class="h-4 w-4" />
                                {{ storeCashout.withdrawal_policy.settings_can_anticipate ? 'Sim' : 'Não' }}
                            </span>
                        </info-col>
                    </div>

                    <!-- Status e Datas -->
                    <div class="mb-5 grid grid-cols-4 gap-3">
                        <info-col title="BLOQUEADO">
                            <span class="inline-flex items-center gap-1" :class="storeCashout.withdrawal_policy.is_blocked ? 'text-red-500' : 'text-green-500'">
                                <component :is="storeCashout.withdrawal_policy.is_blocked ? 'NoSymbolIcon' : 'CheckCircleIcon'" class="h-4 w-4" />
                                {{ storeCashout.withdrawal_policy.is_blocked ? 'Sim' : 'Não' }}
                            </span>
                        </info-col>
                        <info-col title="DORMANT">
                            <span class="inline-flex items-center gap-1" :class="storeCashout.withdrawal_policy.is_dormant ? 'text-yellow-500' : 'text-green-500'">
                                <component :is="storeCashout.withdrawal_policy.is_dormant ? 'ExclamationTriangleIcon' : 'CheckCircleIcon'" class="h-4 w-4" />
                                {{ storeCashout.withdrawal_policy.is_dormant ? 'Sim' : 'Não' }}
                            </span>
                        </info-col>
                        <info-col title="BLOQUEIO PRODUTOS">
                            <span class="inline-flex items-center gap-1" :class="storeCashout.withdrawal_policy.block_products ? 'text-red-500' : 'text-green-500'">
                                <component :is="storeCashout.withdrawal_policy.block_products ? 'NoSymbolIcon' : 'CheckCircleIcon'" class="h-4 w-4" />
                                {{ storeCashout.withdrawal_policy.block_products ? 'Sim' : 'Não' }}
                            </span>
                        </info-col>
                        <info-col title="COOLDOWN ATÉ">
                            {{ storeCashout.withdrawal_policy.cooldown_ends_at ?? 'Sem cooldown' }}
                        </info-col>
                    </div>

                    <!-- Histórico -->
                    <div class="grid grid-cols-4 gap-3">
                        <info-col title="MÉDIA HISTÓRICA APROVADA">
                            {{ storeCashout.withdrawal_policy.avg_cashout_approved_hist_cents != null ? $root.formatCurrency(storeCashout.withdrawal_policy.avg_cashout_approved_hist_cents) : '-' }}
                        </info-col>
                        <info-col title="P90 HISTÓRICO APROVADO">
                            {{ storeCashout.withdrawal_policy.p90_cashout_approved_hist_cents != null ? $root.formatCurrency(storeCashout.withdrawal_policy.p90_cashout_approved_hist_cents) : '-' }}
                        </info-col>
                        <info-col title="TOTAL SAQUES APROVADOS">
                            {{ storeCashout.withdrawal_policy.total_approved_cashouts_hist ?? '-' }}
                        </info-col>
                        <info-col title="REATIVADO EM">
                            {{ storeCashout.withdrawal_policy.reactivated_at ?? '-' }}
                        </info-col>
                    </div>

                    <!-- Motivo do Bloqueio -->
                    <div
                        v-if="storeCashout.withdrawal_policy.block_reason"
                        class="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20"
                    >
                        <div class="flex items-center gap-2">
                            <ExclamationCircleIcon class="h-5 w-5 text-red-500" />
                            <span class="text-sm font-semibold text-red-600 dark:text-red-400">Motivo do Bloqueio</span>
                        </div>
                        <p class="mt-1 pl-7 text-sm text-red-700 dark:text-red-300">
                            {{ storeCashout.withdrawal_policy.block_reason }}
                        </p>
                    </div>

                    <!-- Snapshot -->
                    <div class="mt-4 flex justify-end">
                        <span class="text-xs text-gray-400">
                            Snapshot: {{ storeCashout.withdrawal_policy.snapshot_date ?? '-' }}
                            &middot;
                            Atualizado: {{ storeCashout.withdrawal_policy.updated_at ?? '-' }}
                        </span>
                    </div>
                </div>
            </div>

            <cashout-clicks
                v-if="storeCashout"
                :cashout="storeCashout"
            ></cashout-clicks>
            <cashout-payables
                v-if="storeCashout"
                :cashout="storeCashout"
                products
            ></cashout-payables>
        </div>
        <div>
            <div class="mb-2" v-if="audit">
                <system-analysis v-if="audit && audit.audit && audit.audit.analysis" :analysis="audit?.audit.analysis" />
            </div>
            <card class="mb-2">
                <div class="flex w-full flex-col">
                    <div class="flex justify-between">
                        <span
                            class="mb-1 text-sm font-bold uppercase text-gray-400 dark:text-gray-400"
                        >
                            STATUS
                        </span>
                        <return-cashout
                            v-if="
                                storeCashout &&
                                storeCashout.status === 'approved'
                            "
                            :cashout="storeCashout"
                            @submit="fetchStatus"
                        />
                    </div>

                    <col-skeleton :cols="1" v-if="loading" />

                    <span
                        v-if="!loading"
                        :class="getCashoutStatusClass(storeCashout.status)"
                        class="w-2/5 rounded px-1 py-1 text-center text-sm text-white dark:text-gray-200"
                    >
                        {{ getCashoutStatusName(storeCashout.status) }}
                    </span>
                </div>
            </card>
            <cashout-reviews :cashout="storeCashout" />

            <transfer-status
                v-if="storeCashout"
                class="mb-3"
                :cashout="storeCashout"
            />

            <div
                class="mb-4 flex w-full min-w-0 overflow-hidden rounded-lg bg-white p-5 drop-shadow-sm dark:bg-gray-800"
            >
                <timeline-skeleton v-if="loadingStatus" />
                <ol
                    v-else
                    class="relative w-full border-l border-gray-200 dark:border-gray-700"
                >
                    <li
                        :class="{
                            'mb-5': status.user_id === storeCashout.user_id,
                            'ml-4': true,
                        }"
                        v-for="status in statuses"
                    >
                        <div
                            class="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-white dark:border-gray-900"
                            :class="getStatusClass(status.status)"
                        ></div>
                        <time
                            class="mb-1 text-sm font-normal leading-none text-gray-200 text-gray-800 dark:text-gray-500"
                        >
                            {{ status.created_at }}
                        </time>
                        <h3
                            class="text-md flex items-center font-semibold text-gray-900 dark:text-white"
                        >
                            {{ getCashoutStatusName(status.status) }}
                            <CloudIcon
                                class="ml-1 h-4 h-5 w-4 w-5 text-gray-500 focus-visible:ring-0"
                                v-if="status.user_id === storeCashout.user_id"
                                v-tooltip="'API Itau'"
                            />
                        </h3>
                        <p
                            class="text-base font-normal text-gray-500 dark:text-gray-200"
                            v-if="status.user_id !== storeCashout.user_id"
                        >
                            {{ status.user.name }} {{ status.user.email }}
                        </p>
                        <p
                            v-if="
                                status.description &&
                                status.user_id !== storeCashout.user_id
                            "
                            class="mt-1 rounded-lg bg-slate-900 p-4 text-base font-normal text-gray-500 dark:text-gray-200"
                        >
                            {{ status.description }}
                        </p>
                    </li>
                    <li class="ml-4">
                        <div
                            class="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-white bg-gray-200 dark:border-gray-900 dark:bg-gray-700"
                        ></div>
                        <time
                            class="mb-1 text-sm font-normal leading-none text-gray-900 dark:text-gray-500"
                        >
                            {{ storeCashout.created_at }}
                        </time>
                        <h3
                            class="text-lg font-semibold text-gray-900 dark:text-white"
                        >
                            Saque Solicitado
                        </h3>
                        <p
                            class="text-base font-normal text-gray-500 dark:text-gray-200"
                        >
                            <a
                                @click="approve = !approve"
                                class="inline-flex items-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:cursor-pointer hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:text-blue-700 focus:outline-none focus:ring-4 focus:ring-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white dark:focus:ring-gray-700"
                            >
                                Alterar Status
                                <svg
                                    class="ml-2 h-3 w-3"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                            </a>
                        </p>
                    </li>
                </ol>
            </div>

            <TransitionRoot
                :show="approve"
                enter="transition-opacity duration-150"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="transition-opacity duration-150"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div
                    class="mb-4 w-full min-w-0 overflow-hidden rounded-lg bg-white p-5 drop-shadow-sm dark:bg-gray-800"
                >
                    <h3
                        class="text-lg font-semibold text-gray-900 dark:text-white"
                    >
                        Alterar Status
                    </h3>
                    <label
                        for="countries"
                        class="mb-2 block text-sm font-medium text-gray-900 dark:text-gray-200"
                        >Selecione uma opção</label
                    >
                    <select
                        v-model="form.status"
                        id="countries"
                        class="text-input"
                    >
                        <option :value="false" selected>Status</option>
                        <option value="sent_to_bank">Enviado ao Banco</option>
                        <option value="approved">Aprovado</option>
                        <option value="returned">Retornado</option>
                        <option value="in_audit">Em Auditoria</option>
                        <option
                            v-if="hasPermission('compensate.cashouts')"
                            value="extrajudicial_compensation"
                        >
                            Compensação Por Acordo Extrajudicial
                        </option>
                        <option value="chargeback_penalty">
                            Multa por Excesso de Chargeback
                        </option>
                    </select>

                    <div v-if="form.status === 'reproved'">
                        <label
                            for="message"
                            class="mb-2 mt-2 block text-sm font-medium text-gray-900 dark:text-gray-200"
                            >Motivo</label
                        >
                        <textarea
                            v-model="form.description"
                            id="message"
                            rows="4"
                            class="text-input"
                            placeholder="Your message..."
                        ></textarea>
                    </div>

                    <button
                        v-if="form.status"
                        @click.prevent="updateStatus"
                        :disabled="loadingUpdateStatus"
                        type="button"
                        :class="{ 'cursor-not-allowed': loading }"
                        class="mx-auto mt-5 inline-flex w-full items-center justify-center rounded-md bg-indigo-500 px-4 py-2 text-sm font-semibold leading-6 text-white shadow transition duration-150 ease-in-out hover:bg-indigo-400"
                    >
                        <svg
                            v-if="loadingUpdateStatus"
                            class="-ml-1 mr-3 h-5 w-5 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            ></circle>
                            <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        Enviar
                    </button>
                </div>
            </TransitionRoot>
        </div>
    </div>
</template>

<script>
import VueSelect from "../../../components/forms/VueSelect.vue";
import ProductStatus from "../../../components/layouts/ProductStatus.vue";
import { mapWritableState, mapActions } from "pinia";
import { Store } from "../../../stores/store.js";
import { TransitionRoot } from "@headlessui/vue";
import HelperMixin from "../../../mixins/HelperMixin.vue";
import BankMixin from "../../../mixins/BankMixin.vue";
import CashoutPayables from "./CashoutPayables.vue";
import CashoutClicks from "./CashoutClicks.vue";
import UserHeading from "../../users/UserHeading.vue";
import ReturnCashout from "./ReturnCashout.vue";
import CommissionCards from "../../../components/user/CommissionCards.vue";
import BalanceCards from "../../../components/user/BalanceCards.vue";
import BalanceSchedule from "../user/BalanceSchedule.vue";
import TransferStatus from "../../../components/cashouts/TransferStatus.vue";
import InfoCol from "../../../components/layouts/InfoCol.vue";
import ColSkeleton from "../../../components/layouts/skeleton/ColSkeleton.vue";
import BackButton from "../../../components/layouts/navigation/BackButton.vue";
import TimelineSkeleton from "../../../components/layouts/skeleton/TimelineSkeleton.vue";
import CashoutReviews from "../../../components/cashouts/reviews/CashoutReviews.vue";
import ChargebackRiskBadge from "../../../components/cashouts/reviews/ChargebackRiskBadge.vue";
import Card from "../../../components/layouts/Card.vue";
import CashoutTags from "../../../components/cashouts/CashoutTags.vue";
import {
    ArrowDownTrayIcon,
    CloudIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
    XCircleIcon,
} from "@heroicons/vue/24/outline";
import UpdateBankAccount from "../../../components/finnancial/bankAccount/UpdateBankAccount.vue";
import PermissionMixin from "../../../mixins/PermissionMixin.vue";
import { CheckCircleIcon, NoSymbolIcon } from "@heroicons/vue/24/outline";
import SubmitButton from "../../../components/forms/SubmitButton.vue";
import SystemAnalysis from "../../../components/cashouts/reviews/SystemAnalysis.vue";


export default {
    components: {
        ChargebackRiskBadge,
        ArrowDownTrayIcon,
        XCircleIcon,
        CheckCircleIcon,
        ExclamationTriangleIcon,
        UpdateBankAccount,
        ExclamationCircleIcon,
        CashoutTags,
        Card,
        CashoutReviews,
        TimelineSkeleton,
        BackButton,
        ColSkeleton,
        InfoCol,
        BalanceSchedule,
        BalanceCards,
        VueSelect,
        ProductStatus,
        TransitionRoot,
        CashoutPayables,
        UserHeading,
        ReturnCashout,
        CashoutClicks,
        CommissionCards,
        TransferStatus,
        CloudIcon,
        SubmitButton,
        SystemAnalysis,
        NoSymbolIcon,
    },
    mixins: [HelperMixin, BankMixin, PermissionMixin],
    data() {
        return {
            audit: false,
            approve: false,
            balanceChart: false,
            loadingExport: false,
            loadingStatus: false,
            loadingUpdateStatus: false,
            statuses: [],
            tags: [],
            form: {
                status: false,
                description: "",
            },
        };
    },

    computed: {
        ...mapWritableState(Store, ["storeCashout", "authUser", "feature_flag_is_active"]),

        loading() {
            return !this.storeCashout;
        },

        walletFromCashout() {
            if (!this.storeCashout?.account_uuid) {
                return null;
            }

            return {
                uuid: this.storeCashout.account_uuid,
            };
        },

        totalCashoutAmount() {
            if (!this.storeCashout?.cashout_count) {
                return 0;
            }

            const statuses = ['approved', 'requested', 'sent_to_bank', 'in_audit'];

            return this.storeCashout.cashout_count
                .filter((item) => statuses.includes(item.status))
                .reduce((sum, item) => sum + (item.amount ?? 0), 0);
        },

        pixAutomaticValidation() {
            const validations = this.storeCashout?.bank_account?.bank_account_automatic_validation;

            if (!this.feature_flag_is_active || !validations?.length) {
                return null;
            }

            return validations[0];
        },

        pixValidationCompleted() {
            return this.pixAutomaticValidation?.status === 'completed';
        },

        pixValidationTooltip() {
            if (!this.feature_flag_is_active) {
                return null;
            }

            const validation = this.pixAutomaticValidation;

            if (!validation) {
                return 'Não foi possível realizar validação automática para a chave PIX';
            }

            if (validation.status === 'completed') {
                return `Chave PIX validada via parceiro (${validation.updated_at})`;
            }

            if (validation.status === 'failed') {
                return 'A validação foi mal-sucedida: chave PIX não pertence ao CPF/CNPJ do produtor';
            }

            return 'Não foi possível realizar validação automática para a chave PIX';
        },
    },

    mounted() {
        this.fetchCashout();
        this.fetchStatus();
        this.fetchAudit();
    },

    methods: {
        ...mapActions(Store, ["fetchCashout"]),
        countIcon(status) {
            switch (status) {
                case "approved":
                    return "CheckCircleIcon";
                case "in_audit":
                    return "ExclamationTriangleIcon";
                case "returned":
                    return "XCircleIcon";
                default:
                    return status;
            }
        },
        fetchAudit() {
            axios.get(`/api/cashout/${this.$route.params.cashout_id}/payables/details`)
                .then(({ data }) => {
                    this.audit = data;
                });
        },
        fetchStatus() {
            this.loadingStatus = true;
            axios
                .get(
                    "/api/cashout/" + this.$route.params.cashout_id + "/status",
                )
                .then(({ data }) => {
                    this.statuses = data;
                    this.loadingStatus = false;
                })
                .catch(() => {
                    this.loadingStatus = false;
                });
        },
        exportReconciliation() {
            this.loadingExport = true;
            axios
                .get(
                    `/api/cashout/${this.$route.params.cashout_id}/payables/export-reconciliation`,
                )
                .then(({ data }) => {
                    window.open(data.url, "_blank");
                    this.loadingExport = false;
                })
                .catch(() => {
                    this.loadingExport = false;
                });
        },
        updateStatus() {
            this.loadingUpdateStatus = true;
            axios
                .post(
                    "/api/cashout/" + this.$route.params.cashout_id + "/status",
                    this.form,
                )
                .then(({ data }) => {
                    this.fetchStatus();
                    this.fetchCashout();
                    this.approve = false;
                    this.loadingUpdateStatus = false;
                })
                .catch(() => {
                    this.loadingUpdateStatus = false;
                });
        },
    },
};
</script>
